import { ApiError, type ErrorCode } from './api';
import { t, type Code } from './i18n';

// The plain words for each error code (D6, D22): a refusal's code from the API, or the client's
// own (api.ts). A screen can say something closer to its form first (the login says "Wrong
// username or password." for `invalid` too), then falls back on these.
const MESSAGES: Record<Exclude<ErrorCode, 'locked'>, Code> = {
  invalid: 'error.server',
  invalid_credentials: 'error.credentials',
  // The session ended: the session sends the member to the login, which says so.
  unauthenticated: 'login.loggedOut',
  disabled: 'error.disabled',
  // A one-time password to replace: the session sends the member to the forced change.
  password_change_required: 'password.intro',
  wrong_password: 'error.wrongPassword',
  password_rule: 'error.passwordRule',
  same_password: 'error.samePassword',
  // The admin API's refusals (A4; D23), in the owner's words (A5; D25).
  forbidden: 'error.forbidden',
  username_taken: 'error.usernameTaken',
  own_account: 'error.ownAccount',
  last_admin: 'error.lastAdmin',
  // The events API's (B2; D33), said of an answer in the owner's words (B3; D34). B4's admin
  // screens may say something closer to their forms first.
  event_cancelled: 'error.eventCancelled',
  event_started: 'error.eventStarted',
  too_large: 'error.server',
  not_found: 'error.server',
  server_error: 'error.server',
  network: 'error.network',
  unexpected: 'error.server',
};

/** The lock's full length (D21), for a `locked` answer that came without its wait. */
const LOCK_SECONDS = 15 * 60;

/** "Try again in N minutes": the server's wait in whole minutes, rounded up, a minute at least. */
export function lockedText(seconds: number = LOCK_SECONDS): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return minutes === 1 ? t('error.lockedMinute') : t('error.lockedMinutes', { minutes });
}

/** The words for an error from a call: an ApiError's code, anything else as a server error. */
export function errorText(err: unknown): string {
  if (!(err instanceof ApiError)) return t('error.server');
  if (err.code === 'locked') return lockedText(err.retryAfter);
  return t(MESSAGES[err.code]);
}
